// Keep SDK-injected camera plumbing out of the accessibility tree.
//
// The Mitek MiSnap SDK appends a hidden <div id="mitekMediaContainer">
// holding its <video> element straight onto <body> (scaled to 30%,
// opacity 0, z-index -1 — invisible, but still in the DOM). Screen
// readers walk the DOM, not the paint order, so VoiceOver and TalkBack
// land on that container and announce a "disabled video" over the real
// content (Zendesk #1797865, items #14 and #15). The element is created
// when the SDK starts a capture, is removed and re-created on retries,
// and the SDK rewrites its style attribute while it runs, so a one-off
// attribute set would not stick.
//
// A single MutationObserver on <body> marks the container (and any
// <video> inside it) aria-hidden="true" with tabindex="-1" the moment it
// appears, and again after any re-render. The same helper hides our own
// decorative camera previews, whose feedback is delivered through
// aria-live regions rather than the video itself.

export const SDK_MEDIA_SELECTORS = [
  "#mitekMediaContainer",
  "#mitekMediaContainer video",
];

function hide(el) {
  if (!el || el.nodeType !== 1) return;
  if (el.getAttribute("aria-hidden") !== "true") el.setAttribute("aria-hidden", "true");
  if (el.getAttribute("tabindex") !== "-1") el.setAttribute("tabindex", "-1");
}

/** Apply the attributes to every current match. Returns the count. */
export function hideSdkMediaNow(root = typeof document !== "undefined" ? document : null) {
  if (!root || typeof root.querySelectorAll !== "function") return 0;
  let count = 0;
  for (const selector of SDK_MEDIA_SELECTORS) {
    root.querySelectorAll(selector).forEach((el) => {
      hide(el);
      count += 1;
    });
  }
  return count;
}

/**
 * Watch the document for SDK media containers and hide them from
 * assistive technology as they appear or re-render. Returns a function
 * that stops watching. Safe to call more than once (each call gets its
 * own observer) and a no-op outside the browser.
 */
export function watchSdkMedia(root = typeof document !== "undefined" ? document : null) {
  if (!root || !root.body || typeof MutationObserver === "undefined") return () => {};
  hideSdkMediaNow(root);
  const observer = new MutationObserver((mutations) => {
    let dirty = false;
    for (const m of mutations) {
      if (m.type === "childList" && m.addedNodes.length) {
        dirty = true;
        break;
      }
      if (m.type === "attributes") {
        const t = m.target;
        if (
          t &&
          t.nodeType === 1 &&
          (t.id === "mitekMediaContainer" || (t.closest && t.closest("#mitekMediaContainer")))
        ) {
          dirty = true;
          break;
        }
      }
    }
    if (dirty) hideSdkMediaNow(root);
  });
  observer.observe(root.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-hidden", "tabindex", "style", "id"],
  });
  return () => observer.disconnect();
}
